import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Calendar, ChevronLeft, ChevronRight, Plus, X, List, Grid3x3, Clock, MapPin, Music2, Ticket, Radio, Users, UserPlus, UserCheck, Heart, Share2, ThumbsUp, Filter, ChevronDown } from 'lucide-react';
import { getEventTypeColor, getEventTypeIcon, getMonthName, getDaysInMonth, getFirstDayOfMonth, getEventsForDate as getEventsForDateHelper } from './eventHelpers';
import { renderLiveIndicator, renderOverBadge, renderReactionButtons as renderReactionButtonsComponent } from './eventComponents';
import ShareToWallModal from '../ShareToWallModal';
import DJScheduleModal from './modals/DJScheduleModal';
import EventDetailModal from './modals/EventDetailModal';
import AddEventModal from './modals/AddEventModal';
import ShareModal from './modals/ShareModal';
import PromoNavigationModal from './modals/PromoNavigationModal';

const EventCalendarTab = ({ onBack, autoOpenCalendar, onCalendarOpened, onViewInMyEvents, onDiscordShare }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('grid'); // 'grid', 'list', or 'discovery'
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  
  // Auto-open calendar when navigated from promo
  useEffect(() => {
    if (autoOpenCalendar) {
      setViewMode('grid');
      if (onCalendarOpened) {
        onCalendarOpened();
      }
    }
  }, [autoOpenCalendar, onCalendarOpened]);
  
  // Phase B2: Discovery & User Schedule
  const [djList, setDjList] = useState([]);
  const [selectedDJ, setSelectedDJ] = useState(null);
  const [showDJSchedule, setShowDJSchedule] = useState(false);
  const [djScheduleEvents, setDjScheduleEvents] = useState([]);
  const [relationshipStatus, setRelationshipStatus] = useState(null);
  
  // Discovery filters
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState('a-z'); // 'a-z', 'z-a'
  const [filterEventType, setFilterEventType] = useState('all');
  const [filterGenre, setFilterGenre] = useState('all');
  const [radiusFilter, setRadiusFilter] = useState('all'); // 'all', '10', '25', '50', '100'
  
  // Calendar-specific filters
  const [calendarFilterSource, setCalendarFilterSource] = useState('all'); // 'all', 'my-attending', 'my-posted'
  const [calendarFilterGenre, setCalendarFilterGenre] = useState('all');
  const [showCalendarFilters, setShowCalendarFilters] = useState(false);
  
  // EDM Genre options
  const edmGenres = [
    { value: 'all', label: 'Genres' },
    { value: 'house', label: 'House' },
    { value: 'tech-house', label: 'Tech House' },
    { value: 'deep-house', label: 'Deep House' },
    { value: 'progressive-house', label: 'Progressive House' },
    { value: 'techno', label: 'Techno' },
    { value: 'melodic-techno', label: 'Melodic Techno' },
    { value: 'trance', label: 'Trance' },
    { value: 'psytrance', label: 'Psytrance' },
    { value: 'drum-and-bass', label: 'Drum & Bass' },
    { value: 'dubstep', label: 'Dubstep' },
    { value: 'bass-music', label: 'Bass Music' },
    { value: 'edm', label: 'EDM / Mainstage' },
    { value: 'hardstyle', label: 'Hardstyle' },
    { value: 'hard-techno', label: 'Hard Techno' },
    { value: 'disco', label: 'Disco / Nu-Disco' },
    { value: 'afro-house', label: 'Afro House' },
    { value: 'minimal', label: 'Minimal' },
    { value: 'ambient', label: 'Ambient / Downtempo' },
    { value: 'open-format', label: 'Open Format' },
    { value: 'hip-hop', label: 'Hip Hop' },
    { value: 'various', label: 'Various / Mixed' }
  ];
  
  // Phase B3: Reactions and sharing
  const [userReactions, setUserReactions] = useState({}); // { eventId: { has_liked, has_loved } }
  const [showShareModal, setShowShareModal] = useState(false);
  const [eventToShare, setEventToShare] = useState(null);
  const [wallShareEvent, setWallShareEvent] = useState(null);
  const [shareRecipients, setShareRecipients] = useState([]);
  const [followersList, setFollowersList] = useState([]);
  const [friendsList, setFriendsList] = useState([]);
  
  // Promo modal state
  const [selectedPromo, setSelectedPromo] = useState(null);
  const [showPromoModal, setShowPromoModal] = useState(false);
  
  // Track events with linked promos
  const [eventsWithPromos, setEventsWithPromos] = useState(new Set());

  // Form state
  const [eventForm, setEventForm] = useState({
    event_name: '',
    venue_name: '',
    event_type: 'club-show',
    genre: 'house',
    location: '',
    event_date: '',
    event_time: '',
    description: '',
    ticket_url: '',
    stream_url: '',
    is_recurring: false,
    recurring_type: 'weekly',
    recurring_day: ''
  });
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState(false);

  // Load events for current month
  useEffect(() => {
    loadEvents();
  }, [currentDate]);

  // Listen for djBoothOpenEventCalendar event to navigate to the event's month
  useEffect(() => {
    const handleOpenEventCalendar = async (event) => {
      const { eventId, eventDate, tab } = event.detail || {};
      console.log('📅 EventCalendarTab received djBoothOpenEventCalendar event. eventId:', eventId, 'eventDate:', eventDate, 'tab:', tab);
      
      // If eventDate is provided directly (from Festival Events), navigate to that month
      if (eventDate && !eventId) {
        console.log('📅 Navigating to date from Festival Events:', eventDate);
        try {
          // Parse the date string - handle both ISO and local date formats
          let targetDate;
          if (typeof eventDate === 'string') {
            // Extract just the date part to avoid timezone issues
            const dateMatch = eventDate.match(/^(\d{4})-(\d{2})-(\d{2})/);
            if (dateMatch) {
              targetDate = new Date(parseInt(dateMatch[1]), parseInt(dateMatch[2]) - 1, parseInt(dateMatch[3]));
            } else {
              targetDate = new Date(eventDate);
            }
          } else {
            targetDate = new Date(eventDate);
          }
          console.log('📅 Setting calendar to month:', targetDate.toLocaleDateString());
          setCurrentDate(targetDate);
          setViewMode('grid'); // Switch to grid view to see the calendar
        } catch (e) {
          console.error('📅 Error parsing eventDate:', e);
        }
        return;
      }
      
      if (eventId) {
        // Try to find the event in current events list
        let eventToOpen = events.find(e => e.id === eventId);
        
        if (!eventToOpen) {
          console.log('📅 Event not in list, fetching from backend...');
          // If not found in current list, fetch it from backend
          try {
            const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
            const response = await fetch(`${BACKEND_URL}/api/dj-events/${eventId}`);
            if (response.ok) {
              eventToOpen = await response.json();
              console.log('📅 Fetched event from backend:', eventToOpen.event_name);
            }
          } catch (error) {
            console.error('Failed to fetch event:', error);
          }
        } else {
          console.log('📅 Found event in list:', eventToOpen.event_name);
        }
        
        if (eventToOpen) {
          // Navigate to the month of the event
          const eventDateString = eventToOpen.event_date || eventToOpen.date;
          if (eventDateString) {
            const eventDateObj = new Date(eventDateString);
            console.log('📅 Event date string:', eventDateString);
            console.log('📅 Parsed date object:', eventDateObj);
            console.log('📅 Navigating calendar to month:', eventDateObj.toLocaleDateString());
            setCurrentDate(eventDateObj);
          } else {
            console.error('📅 No date found on event object:', eventToOpen);
          }
          // Don't open the modal, just show it on the grid
        }
      }
    };

    window.addEventListener('djBoothOpenEventCalendar', handleOpenEventCalendar);
    return () => {
      window.removeEventListener('djBoothOpenEventCalendar', handleOpenEventCalendar);
    };
  }, [events]);

  const loadEvents = async () => {
    setLoading(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const month = currentDate.getMonth() + 1;
      const year = currentDate.getFullYear();
      
      const response = await fetch(`${BACKEND_URL}/api/dj-events?month=${month}&year=${year}`);
      if (response.ok) {
        const data = await response.json();
        setEvents(data);
        
        // Fetch all promos to check which events have linked promos
        try {
          const promosResponse = await fetch(`${BACKEND_URL}/api/promos?category=event&limit=1000`);
          if (promosResponse.ok) {
            const promosData = await promosResponse.json();
            if (promosData.success) {
              // Create a set of event IDs that have promos
              const eventIdsWithPromos = new Set(
                promosData.promos
                  .filter(promo => promo.linked_event_id)
                  .map(promo => promo.linked_event_id)
              );
              setEventsWithPromos(eventIdsWithPromos);
            }
          }
        } catch (promoError) {
          console.error('Failed to fetch promos for indicator:', promoError);
        }
      }
    } catch (error) {
      console.error('Failed to load events:', error);
    } finally {
      setLoading(false);
    }
  };

  // Phase B2: Load DJ list with event counts
  const loadDJList = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/dj-events/users-with-schedules`);
      if (response.ok) {
        const data = await response.json();
        setDjList(data);
      }
    } catch (error) {
      console.error('Failed to load DJ list:', error);
    }
  };

  // Phase B2: Load a DJ's full schedule
  const loadDJSchedule = async (userId) => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/dj-events?user_id=${userId}`);
      if (response.ok) {
        const data = await response.json();
        setDjScheduleEvents(data);
      }
    } catch (error) {
      console.error('Failed to load DJ schedule:', error);
    }
  };

  // Phase B2: Get relationship status with a DJ
  const loadRelationshipStatus = async (userId) => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token');
      if (!token) {
        console.log('No token found, setting default relationship status');
        setRelationshipStatus({
          is_self: false,
          is_following: false,
          is_followed_by: false,
          is_friend: false,
          friend_request_pending: false,
          friend_request_sent: false
        });
        return;
      }

      const response = await fetch(`${BACKEND_URL}/api/users/${userId}/relationship-status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        console.log('Relationship status loaded:', data);
        setRelationshipStatus(data);
      } else {
        console.error('Failed to load relationship status:', response.status);
        // Set default values on error
        setRelationshipStatus({
          is_self: false,
          is_following: false,
          is_followed_by: false,
          is_friend: false,
          friend_request_pending: false,
          friend_request_sent: false
        });
      }
    } catch (error) {
      console.error('Failed to load relationship status:', error);
      // Set default values on error
      setRelationshipStatus({
        is_self: false,
        is_following: false,
        is_followed_by: false,
        is_friend: false,
        friend_request_pending: false,
        friend_request_sent: false
      });
    }
  };

  // Phase B2: Follow a DJ
  const handleFollow = async (userId) => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token');
      if (!token) {
        alert('Please log in to follow DJs');
        return;
      }

      const response = await fetch(`${BACKEND_URL}/api/users/${userId}/follow`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        loadRelationshipStatus(userId);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to follow user');
      }
    } catch (error) {
      console.error('Failed to follow user:', error);
    }
  };

  // Phase B2: Unfollow a DJ
  const handleUnfollow = async (userId) => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${BACKEND_URL}/api/users/${userId}/follow`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        loadRelationshipStatus(userId);
      }
    } catch (error) {
      console.error('Failed to unfollow user:', error);
    }
  };

  // Phase B2: Send friend request
  const handleFriendRequest = async (userId) => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token');
      if (!token) {
        alert('Please log in to send friend requests');
        return;
      }

      const response = await fetch(`${BACKEND_URL}/api/users/${userId}/friend-request`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        loadRelationshipStatus(userId);
        alert('Friend request sent!');
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to send friend request');
      }
    } catch (error) {
      console.error('Failed to send friend request:', error);
    }
  };

  // Phase B2: Load DJ list when switching to discovery view
  useEffect(() => {
    if (viewMode === 'discovery') {
      loadDJList();
    }
  }, [viewMode]);

  // Phase B3: Load user reactions when event modal opens
  useEffect(() => {
    if (selectedEvent && showEventModal) {
      loadUserReactions(selectedEvent.id);
    }
  }, [selectedEvent, showEventModal]);

  // Filter and sort DJ list
  const getFilteredDJList = () => {
    let filtered = [...djList];

    // Search filter
    if (searchQuery.trim()) {
      filtered = filtered.filter(dj => 
        dj.user_name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Sort
    if (sortOrder === 'a-z') {
      filtered.sort((a, b) => a.user_name.localeCompare(b.user_name));
    } else if (sortOrder === 'z-a') {
      filtered.sort((a, b) => b.user_name.localeCompare(a.user_name));
    }

    // Note: Event type, genre, and radius filters would require additional data
    // from backend (DJ's event types, genres, location). These can be added when
    // the backend provides this information.

    return filtered;
  };

  // Phase B3: Load user's reactions for an event
  const loadUserReactions = async (eventId) => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${BACKEND_URL}/api/dj-events/${eventId}/my-reactions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUserReactions(prev => ({
          ...prev,
          [eventId]: data
        }));
      }
    } catch (error) {
      console.error('Failed to load user reactions:', error);
    }
  };

  // Phase B3: Handle like
  const handleLike = async (eventId) => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token');
      if (!token) {
        alert('Please log in to like events');
        return;
      }

      const isLiked = userReactions[eventId]?.has_liked;
      const method = isLiked ? 'DELETE' : 'POST';
      
      const response = await fetch(`${BACKEND_URL}/api/dj-events/${eventId}/like`, {
        method,
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        // Update the event in the list
        setEvents(prevEvents => prevEvents.map(e => 
          e.id === eventId ? { ...e, likes: data.likes } : e
        ));
        setDjScheduleEvents(prevEvents => prevEvents.map(e => 
          e.id === eventId ? { ...e, likes: data.likes } : e
        ));
        if (selectedEvent?.id === eventId) {
          setSelectedEvent({ ...selectedEvent, likes: data.likes });
        }
        // Update user reactions
        loadUserReactions(eventId);
      } else {
        const error = await response.json();
        console.error('Like failed:', error.detail);
      }
    } catch (error) {
      console.error('Failed to like event:', error);
    }
  };

  // Phase B3: Handle love
  const handleLove = async (eventId) => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token');
      if (!token) {
        alert('Please log in to love events');
        return;
      }

      const isLoved = userReactions[eventId]?.has_loved;
      const method = isLoved ? 'DELETE' : 'POST';
      
      const response = await fetch(`${BACKEND_URL}/api/dj-events/${eventId}/love`, {
        method,
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        // Update the event in the list
        setEvents(prevEvents => prevEvents.map(e => 
          e.id === eventId ? { ...e, loves: data.loves } : e
        ));
        setDjScheduleEvents(prevEvents => prevEvents.map(e => 
          e.id === eventId ? { ...e, loves: data.loves } : e
        ));
        if (selectedEvent?.id === eventId) {
          setSelectedEvent({ ...selectedEvent, loves: data.loves });
        }
        // Update user reactions
        loadUserReactions(eventId);
      } else {
        const error = await response.json();
        console.error('Love failed:', error.detail);
      }
    } catch (error) {
      console.error('Failed to love event:', error);
    }
  };

  // Phase B3: Handle share
  const handleShareClick = async (event) => {
    setEventToShare(event);
    setShowShareModal(true);
    
    // Load followers and friends for sharing
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token');
      if (!token) return;

      const userDataStr = localStorage.getItem('user');
      if (!userDataStr) return;
      const userData = JSON.parse(userDataStr);

      const response = await fetch(`${BACKEND_URL}/api/users/${userData.id}/relationships`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setFollowersList(data.followers || []);
        setFriendsList(data.friends || []);
      }
    } catch (error) {
      console.error('Failed to load relationships:', error);
    }
  };

  // Phase B3: Submit share
  const handleShareSubmit = async () => {
    if (shareRecipients.length === 0) {
      alert('Please select at least one person to share with');
      return;
    }

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${BACKEND_URL}/api/dj-events/${eventToShare.id}/share`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(shareRecipients)
      });

      if (response.ok) {
        const data = await response.json();
        // Update share count
        setEvents(prevEvents => prevEvents.map(e => 
          e.id === eventToShare.id ? { ...e, shares: data.shares } : e
        ));
        setDjScheduleEvents(prevEvents => prevEvents.map(e => 
          e.id === eventToShare.id ? { ...e, shares: data.shares } : e
        ));
        if (selectedEvent?.id === eventToShare.id) {
          setSelectedEvent({ ...selectedEvent, shares: data.shares });
        }
        
        setShowShareModal(false);
        setEventToShare(null);
        setShareRecipients([]);
        alert('Event shared successfully!');
      }
    } catch (error) {
      console.error('Failed to share event:', error);
      alert('Failed to share event');
    }
  };

  // Calendar helpers
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    return { daysInMonth, startingDayOfWeek };
  };

  // Get current user ID from localStorage
  const getCurrentUserId = () => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        return user.id;
      }
    } catch (e) {
      console.error('Error getting user ID:', e);
    }
    return null;
  };

  const getEventsForDate = (day) => {
    const targetYear = currentDate.getFullYear();
    const targetMonth = currentDate.getMonth() + 1;
    const targetDay = day;
    const currentUserId = getCurrentUserId();
    
    return events.filter(event => {
      const eventDateStr = event.event_date;
      if (!eventDateStr) return false;
      
      // Parse date without timezone conversion - extract just the date parts
      // This handles both ISO strings like "2025-12-06T00:00:00Z" and "2025-12-06"
      let eventYear, eventMonth, eventDay;
      
      if (typeof eventDateStr === 'string') {
        // Extract YYYY-MM-DD from the string (handles both ISO and date-only formats)
        const dateMatch = eventDateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (dateMatch) {
          eventYear = parseInt(dateMatch[1], 10);
          eventMonth = parseInt(dateMatch[2], 10);
          eventDay = parseInt(dateMatch[3], 10);
        } else {
          // Fallback to Date parsing if format doesn't match
          const eventDate = new Date(eventDateStr);
          eventYear = eventDate.getUTCFullYear();
          eventMonth = eventDate.getUTCMonth() + 1;
          eventDay = eventDate.getUTCDate();
        }
      } else {
        // It's already a Date object - use UTC to avoid timezone issues
        const eventDate = new Date(eventDateStr);
        eventYear = eventDate.getUTCFullYear();
        eventMonth = eventDate.getUTCMonth() + 1;
        eventDay = eventDate.getUTCDate();
      }
      
      // Check if date matches
      const dateMatches = eventYear === targetYear && eventMonth === targetMonth && eventDay === targetDay;
      if (!dateMatches) return false;
      
      // Apply source filter
      if (calendarFilterSource !== 'all') {
        if (calendarFilterSource === 'my-attending') {
          // Only show festival_attendance events (events user is "going" to)
          if (event.event_type !== 'festival_attendance') return false;
        } else if (calendarFilterSource === 'my-posted') {
          // Only show events created by the current user (not festival_attendance)
          if (event.event_type === 'festival_attendance') return false;
          if (event.user_id !== currentUserId) return false;
        }
      }
      
      // Apply genre filter
      if (calendarFilterGenre !== 'all') {
        const eventGenre = (event.genre || '').toLowerCase().replace(/\s+/g, '-');
        if (eventGenre !== calendarFilterGenre && eventGenre !== calendarFilterGenre.replace(/-/g, '')) return false;
      }
      
      return true;
    });
  };

  const changeMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const getEventTypeIcon = (type) => {
    switch(type) {
      case 'club-show': return '🎧';
      case 'festival': return '🎪';
      case 'festival_attendance': return '🎫';  // Ticket emoji for "I'm Going" events
      case 'livestream': return '📡';
      case 'private-event': return '💒';
      case 'demo_deadline': return '📧';
      case 'contest_deadline': return '🏆';
      default: return '🎵';
    }
  };

  // Helper function wrapper to pass handlers to renderReactionButtons
  const renderReactionButtons = (event, size = 'small') => {
    return renderReactionButtonsComponent(event, size, userReactions, {
      loadUserReactions,
      handleLike,
      handleLove,
      handleShareClick,
      handleWallShareClick: (ev) => setWallShareEvent(ev)
    });
  };

  const handleFormChange = (field, value) => {
    setEventForm(prev => ({ ...prev, [field]: value }));
    setFormError('');
  };

  const handleSubmitEvent = async () => {
    // Validation
    if (!eventForm.event_name.trim()) {
      setFormError('Event name is required');
      return;
    }
    if (!eventForm.venue_name.trim()) {
      setFormError('Venue name is required');
      return;
    }
    if (!eventForm.location.trim()) {
      setFormError('Location is required');
      return;
    }
    if (!eventForm.event_date) {
      setFormError('Event date is required');
      return;
    }
    if (!eventForm.event_time.trim()) {
      setFormError('Event time is required');
      return;
    }
    if (!eventForm.description.trim()) {
      setFormError('Description is required');
      return;
    }

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token');
      
      if (!token) {
        setFormError('You must be logged in to add an event');
        return;
      }

      const payload = {
        ...eventForm,
        event_date: new Date(eventForm.event_date).toISOString()
      };

      const response = await fetch(`${BACKEND_URL}/api/dj-events`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create event');
      }

      const result = await response.json();
      
      // Success!
      setFormSuccess(true);
      setTimeout(() => {
        setShowAddModal(false);
        setFormSuccess(false);
        setEventForm({
          event_name: '',
          venue_name: '',
          event_type: 'club-show',
          genre: 'house',
          location: '',
          event_date: '',
          event_time: '',
          description: '',
          ticket_url: '',
          stream_url: '',
          is_recurring: false,
          recurring_type: 'weekly',
          recurring_day: ''
        });
        loadEvents(); // Reload events
      }, 2000);

      if (result.recurring_count > 0) {
        alert(`Event created! ${result.recurring_count} recurring events also added.`);
      }
    } catch (error) {
      setFormError(error.message);
    }
  };

  const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentDate);
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

  return (
    <div className="event-calendar-tab" style={{
      display: 'flex',
      flexDirection: 'column',
      height: 'calc(100vh - 220px)',
      maxHeight: '75vh',
      padding: '0 2rem 2rem 2rem'
    }}>
      {/* Header */}
      <div className="event-calendar-header" style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginTop: '-1rem',
        marginBottom: '0.5rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <Calendar size={28} style={{ color: '#00ffff' }} />
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: '700',
              background: 'linear-gradient(135deg, #00ffff, #ff00ff)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              margin: 0,
              whiteSpace: 'nowrap'
            }}>
              Event Calendar
            </h2>
          </div>
        </div>

        <span
          onClick={() => setShowAddModal(true)}
          style={{
            color: 'rgba(255, 255, 255, 0.8)',
            fontSize: '0.95rem',
            cursor: 'pointer',
            transition: 'color 0.2s ease',
            whiteSpace: 'nowrap',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = '#00ffff';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = 'rgba(255, 255, 255, 0.8)';
          }}
        >
          <Plus size={18} /> Add Event
        </span>
      </div>

      {/* Back Link */}
      <div
        className="event-calendar-back"
        onClick={onBack}
        style={{
          color: 'rgba(255, 255, 255, 0.6)',
          fontSize: '0.85rem',
          cursor: 'pointer',
          transition: 'color 0.2s ease',
          textDecoration: 'underline',
          marginBottom: '0.75rem'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.color = '#00ffff';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = 'rgba(255, 255, 255, 0.6)';
        }}
      >
        ← Back to Gig & Event Hub
      </div>

      {/* Calendar Controls */}
      <div className="event-calendar-controls" style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '1rem',
        padding: '1rem',
        background: 'rgba(0, 0, 0, 0.3)',
        borderRadius: '12px'
      }}>
        <div className="event-calendar-month-nav" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button
            onClick={() => changeMonth(-1)}
            style={{
              background: 'rgba(0, 255, 255, 0.1)',
              border: '1px solid rgba(0, 255, 255, 0.3)',
              borderRadius: '8px',
              padding: '0.5rem',
              color: '#00ffff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <ChevronLeft size={20} />
          </button>
          
          <h3 style={{
            fontSize: '1.5rem',
            fontWeight: '700',
            color: '#fff',
            margin: 0,
            minWidth: '200px',
            textAlign: 'center'
          }}>
            {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
          </h3>
          
          <button
            onClick={() => changeMonth(1)}
            style={{
              background: 'rgba(0, 255, 255, 0.1)',
              border: '1px solid rgba(0, 255, 255, 0.3)',
              borderRadius: '8px',
              padding: '0.5rem',
              color: '#00ffff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <ChevronRight size={20} />
          </button>

          <button
            onClick={goToToday}
            style={{
              background: 'rgba(255, 0, 255, 0.1)',
              border: '1px solid rgba(255, 0, 255, 0.3)',
              borderRadius: '8px',
              padding: '0.5rem 1rem',
              color: '#ff00ff',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: '600'
            }}
          >
            {new Date().toLocaleDateString('en-US', { weekday: 'long' })}
          </button>
        </div>

        {/* View Toggle with Filters on the left */}
        <div className="event-calendar-view-toggle" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          {/* Events Filter Dropdown - Simple select */}
          {(viewMode === 'grid' || viewMode === 'list') && (
            <select
              value={calendarFilterSource}
              onChange={(e) => setCalendarFilterSource(e.target.value)}
              style={{
                padding: '0.5rem 0.75rem',
                background: calendarFilterSource !== 'all' 
                  ? 'rgba(255, 0, 255, 0.2)' 
                  : '#000000',
                border: `1px solid ${calendarFilterSource !== 'all' 
                  ? '#ff00ff' 
                  : 'rgba(255, 255, 255, 0.2)'}`,
                borderRadius: '8px',
                color: calendarFilterSource !== 'all' 
                  ? '#ff00ff' 
                  : 'rgba(255, 255, 255, 0.9)',
                cursor: 'pointer',
                fontSize: '0.85rem',
                fontWeight: '500',
                outline: 'none'
              }}
            >
              <option value="all" style={{ background: '#000000' }}>Events</option>
              <option value="my-attending" style={{ background: '#000000' }}>🎫 My Attending Events</option>
              <option value="my-posted" style={{ background: '#000000' }}>📝 My Posted Events</option>
            </select>
          )}
          
          {/* Grid/List/Browse Toggle */}
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={() => setViewMode('grid')}
              style={{
                background: viewMode === 'grid' ? 'rgba(0, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.3)',
                border: `1px solid ${viewMode === 'grid' ? '#00ffff' : 'rgba(255, 255, 255, 0.1)'}`,
                borderRadius: '8px',
                padding: '0.5rem',
                color: viewMode === 'grid' ? '#00ffff' : 'rgba(255, 255, 255, 0.6)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.9rem'
              }}
            >
              <Grid3x3 size={18} /> Grid
            </button>
            <button
              onClick={() => setViewMode('list')}
              style={{
                background: viewMode === 'list' ? 'rgba(0, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.3)',
                border: `1px solid ${viewMode === 'list' ? '#00ffff' : 'rgba(255, 255, 255, 0.1)'}`,
                borderRadius: '8px',
                padding: '0.5rem',
                color: viewMode === 'list' ? '#00ffff' : 'rgba(255, 255, 255, 0.6)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.9rem'
              }}
            >
              <List size={18} /> List
            </button>
            <button
              onClick={() => setViewMode('discovery')}
              style={{
                background: viewMode === 'discovery' ? 'rgba(255, 0, 255, 0.2)' : 'rgba(0, 0, 0, 0.3)',
                border: `1px solid ${viewMode === 'discovery' ? '#ff00ff' : 'rgba(255, 255, 255, 0.1)'}`,
                borderRadius: '8px',
                padding: '0.5rem',
                color: viewMode === 'discovery' ? '#ff00ff' : 'rgba(255, 255, 255, 0.6)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.9rem'
              }}
            >
              <Users size={18} /> Browse DJs
            </button>
          </div>
        </div>
      </div>

      {/* Active Filter Tags - shown when filters are applied but dropdown is closed */}
      {(viewMode === 'grid' || viewMode === 'list') && (calendarFilterSource !== 'all' || calendarFilterGenre !== 'all') && !showCalendarFilters && (
        <div style={{
          marginBottom: '0.75rem',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '0.5rem'
        }}>
              {calendarFilterSource !== 'all' && (
                <span style={{
                  padding: '0.25rem 0.75rem',
                  background: 'rgba(255, 0, 255, 0.2)',
                  border: '1px solid rgba(255, 0, 255, 0.4)',
                  borderRadius: '20px',
                  fontSize: '0.75rem',
                  color: '#ff00ff',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  {calendarFilterSource === 'my-attending' ? '🎫 My Attending' : '📅 My Posted'}
                  <button
                    onClick={() => setCalendarFilterSource('all')}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#ff00ff',
                      cursor: 'pointer',
                      padding: 0,
                      fontSize: '1rem',
                      lineHeight: 1
                    }}
                  >×</button>
                </span>
              )}
              {calendarFilterGenre !== 'all' && (
                <span style={{
                  padding: '0.25rem 0.75rem',
                  background: 'rgba(0, 255, 255, 0.2)',
                  border: '1px solid rgba(0, 255, 255, 0.4)',
                  borderRadius: '20px',
                  fontSize: '0.75rem',
                  color: '#00ffff',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  🎵 {edmGenres.find(g => g.value === calendarFilterGenre)?.label || calendarFilterGenre}
                  <button
                    onClick={() => setCalendarFilterGenre('all')}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#00ffff',
                      cursor: 'pointer',
                      padding: 0,
                      fontSize: '1rem',
                      lineHeight: 1
                    }}
                  >×</button>
                </span>
              )}
        </div>
      )}

      {/* Calendar View */}
      {viewMode === 'grid' && (
        <div className="event-calendar-grid-wrap" style={{
          flex: 1,
          overflowY: 'auto',
          background: 'rgba(0, 0, 0, 0.2)',
          borderRadius: '12px',
          padding: '4px'
        }}>
          {/* Calendar Days Grid - 7 columns */}
          <div className="event-calendar-days-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: '4px',
            width: '100%'
          }}>
            {/* Day cells */}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const dayEvents = getEventsForDate(day);
              const isToday = new Date().getDate() === day &&
                            new Date().getMonth() === currentDate.getMonth() &&
                            new Date().getFullYear() === currentDate.getFullYear();

              return (
                <div
                  key={day}
                  className="event-calendar-day-cell"
                  style={{
                    minHeight: '85px',
                    background: 'rgba(255, 255, 255, 0.02)',
                    border: isToday ? '2px solid #ff00ff' : '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '4px',
                    padding: '4px',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                  }}
                >
                  {/* Day number */}
                  <div style={{
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    color: isToday ? '#ff00ff' : 'rgba(255, 255, 255, 0.8)',
                    marginBottom: '4px'
                  }}>
                    {day}
                  </div>
                  
                  {/* Events container */}
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '2px',
                    overflow: 'hidden',
                    flex: 1
                  }}>
                    {dayEvents.slice(0, 3).map((event) => (
                      <div
                        key={event.id}
                        onClick={() => {
                          setSelectedEvent(event);
                          setShowEventModal(true);
                        }}
                        style={{
                          padding: '2px 4px',
                          background: `${getEventTypeColor(event.event_type)}60`,
                          border: `1px solid ${getEventTypeColor(event.event_type)}`,
                          borderRadius: '3px',
                          fontSize: '0.6rem',
                          color: '#fff',
                          cursor: 'pointer',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '2px'
                        }}
                      >
                        <span>{getEventTypeIcon(event.event_type)}</span>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {event.event_name}
                        </span>
                      </div>
                    ))}
                    {dayEvents.length > 3 && (
                      <div style={{
                        fontSize: '0.5rem',
                        color: 'rgba(255, 255, 255, 0.5)',
                        textAlign: 'center'
                      }}>
                        +{dayEvents.length - 3} more
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <div style={{
          flex: 1,
          overflowY: 'auto',
          background: 'rgba(0, 0, 0, 0.2)',
          borderRadius: '12px',
          padding: '1rem'
        }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: 'rgba(255, 255, 255, 0.6)' }}>
              Loading events...
            </div>
          ) : events.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: 'rgba(255, 255, 255, 0.6)' }}>
              No events this month
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {events.map(event => {
                const eventDate = new Date(event.event_date);
                return (
                  <div
                    key={event.id}
                    onClick={() => {
                      setSelectedEvent(event);
                      setShowEventModal(true);
                    }}
                    style={{
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: `2px solid ${getEventTypeColor(event.event_type)}`,
                      borderRadius: '12px',
                      padding: '1.5rem',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = `${getEventTypeColor(event.event_type)}20`;
                      e.currentTarget.style.transform = 'translateY(-4px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                      <div>
                        <h3 style={{
                          fontSize: '1.25rem',
                          fontWeight: '700',
                          color: '#fff',
                          margin: '0 0 0.5rem 0',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.75rem',
                          flexWrap: 'wrap'
                        }}>
                          <span>{getEventTypeIcon(event.event_type)} {event.event_name}</span>
                          {event.is_live ? renderLiveIndicator(event, 'small') : event.is_over ? renderOverBadge(event, 'small') : null}
                        </h3>
                        <p style={{
                          fontSize: '1rem',
                          color: getEventTypeColor(event.event_type),
                          margin: 0
                        }}>
                          {event.venue_name}
                        </p>
                      </div>
                      <span style={{
                        padding: '0.5rem 1rem',
                        background: `${getEventTypeColor(event.event_type)}30`,
                        borderRadius: '8px',
                        fontSize: '0.85rem',
                        fontWeight: '600',
                        color: '#fff',
                        flexShrink: 0
                      }}>
                        {event.event_type.replace('-', ' ').toUpperCase()}
                      </span>
                    </div>
                    
                    <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
                      <span style={{ color: 'rgba(255, 255, 255, 0.8)', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem' }}>
                        <Calendar size={16} /> {eventDate.toLocaleDateString()}
                      </span>
                      <span style={{ color: 'rgba(255, 255, 255, 0.8)', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem' }}>
                        <Clock size={16} /> {event.event_time}
                      </span>
                      <span style={{ color: 'rgba(255, 255, 255, 0.8)', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem' }}>
                        <MapPin size={16} /> {event.location}
                      </span>
                      <span style={{ color: 'rgba(255, 255, 255, 0.8)', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem' }}>
                        <Music2 size={16} /> {event.genre}
                      </span>
                    </div>
                    
                    {/* Reaction Buttons */}
                    {renderReactionButtons(event, 'small')}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Discovery View */}
      {viewMode === 'discovery' && (
        /* Discovery View - Browse by DJ */
        <div style={{
          flex: 1,
          overflowY: 'auto',
          background: 'rgba(0, 0, 0, 0.2)',
          borderRadius: '12px',
          padding: '1.5rem'
        }}>
          <h3 style={{
            fontSize: '1.5rem',
            fontWeight: '700',
            color: '#fff',
            marginBottom: '1.5rem',
            background: 'linear-gradient(135deg, #ff00ff, #00ffff)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            🎧 Browse DJ Schedules
          </h3>

          {/* Search and Filters */}
          <div style={{
            background: 'rgba(0, 0, 0, 0.3)',
            borderRadius: '12px',
            padding: '1.5rem',
            marginBottom: '1.5rem',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            {/* Search Bar */}
            <div style={{ marginBottom: '1rem' }}>
              <input
                type="text"
                placeholder="Search DJs by name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  background: 'rgba(0, 0, 0, 0.4)',
                  border: '1px solid rgba(0, 255, 255, 0.3)',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '1rem',
                  outline: 'none'
                }}
                onFocus={(e) => {
                  e.target.style.border = '1px solid rgba(0, 255, 255, 0.6)';
                }}
                onBlur={(e) => {
                  e.target.style.border = '1px solid rgba(0, 255, 255, 0.3)';
                }}
              />
            </div>

            {/* Filters Row */}
            <div className="event-calendar-discovery-filters" style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '1rem'
            }}>
              {/* Sort Order */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.85rem',
                  color: 'rgba(255, 255, 255, 0.7)',
                  marginBottom: '0.5rem',
                  fontWeight: '600'
                }}>
                  Sort By
                </label>
                <select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    background: 'rgba(0, 0, 0, 0.4)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '0.9rem',
                    cursor: 'pointer'
                  }}
                >
                  <option value="a-z">A-Z</option>
                  <option value="z-a">Z-A</option>
                </select>
              </div>

              {/* Event Type Filter */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.85rem',
                  color: 'rgba(255, 255, 255, 0.7)',
                  marginBottom: '0.5rem',
                  fontWeight: '600'
                }}>
                  Event Type
                </label>
                <select
                  value={filterEventType}
                  onChange={(e) => setFilterEventType(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    background: 'rgba(0, 0, 0, 0.4)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '0.9rem',
                    cursor: 'pointer'
                  }}
                >
                  <option value="all">Types</option>
                  <option value="club-show">Club/Bar Show</option>
                  <option value="festival">Festival</option>
                  <option value="livestream">Livestream</option>
                  <option value="private-event">Private Event</option>
                </select>
              </div>

              {/* Genre Filter */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.85rem',
                  color: 'rgba(255, 255, 255, 0.7)',
                  marginBottom: '0.5rem',
                  fontWeight: '600'
                }}>
                  Genre
                </label>
                <select
                  value={filterGenre}
                  onChange={(e) => setFilterGenre(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    background: 'rgba(0, 0, 0, 0.4)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '0.9rem',
                    cursor: 'pointer'
                  }}
                >
                  <option value="all">Genres</option>
                  <option value="house">House</option>
                  <option value="techno">Techno</option>
                  <option value="hip-hop">Hip Hop</option>
                  <option value="edm">EDM</option>
                  <option value="open-format">Open Format</option>
                  <option value="drum-and-bass">Drum & Bass</option>
                  <option value="dubstep">Dubstep</option>
                  <option value="trance">Trance</option>
                </select>
              </div>

              {/* Radius Filter */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.85rem',
                  color: 'rgba(255, 255, 255, 0.7)',
                  marginBottom: '0.5rem',
                  fontWeight: '600'
                }}>
                  Distance
                </label>
                <select
                  value={radiusFilter}
                  onChange={(e) => setRadiusFilter(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    background: 'rgba(0, 0, 0, 0.4)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '0.9rem',
                    cursor: 'pointer'
                  }}
                >
                  <option value="all">Locations</option>
                  <option value="10">Within 10 miles</option>
                  <option value="25">Within 25 miles</option>
                  <option value="50">Within 50 miles</option>
                  <option value="100">Within 100 miles</option>
                </select>
              </div>
            </div>

            {/* Active Filter Badges */}
            {(searchQuery || sortOrder !== 'a-z' || filterEventType !== 'all' || filterGenre !== 'all' || radiusFilter !== 'all') && (
              <div style={{
                marginTop: '1rem',
                padding: '0.75rem',
                background: 'rgba(0, 255, 255, 0.05)',
                border: '1px solid rgba(0, 255, 255, 0.2)',
                borderRadius: '8px',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '0.5rem',
                alignItems: 'center'
              }}>
                <span style={{ fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.6)', marginRight: '0.5rem' }}>
                  Active:
                </span>

                {/* Search Badge */}
                {searchQuery && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.35rem 0.75rem',
                    background: 'rgba(0, 255, 255, 0.2)',
                    border: '1px solid rgba(0, 255, 255, 0.4)',
                    borderRadius: '20px',
                    fontSize: '0.8rem',
                    color: '#00ffff'
                  }}>
                    <span>Search: &ldquo;{searchQuery}&rdquo;</span>
                    <button
                      onClick={() => setSearchQuery('')}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#00ffff',
                        cursor: 'pointer',
                        padding: 0,
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: '1rem',
                        fontWeight: 'bold'
                      }}
                    >
                      ×
                    </button>
                  </div>
                )}

                {/* Sort Badge */}
                {sortOrder !== 'a-z' && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.35rem 0.75rem',
                    background: 'rgba(0, 255, 255, 0.2)',
                    border: '1px solid rgba(0, 255, 255, 0.4)',
                    borderRadius: '20px',
                    fontSize: '0.8rem',
                    color: '#00ffff'
                  }}>
                    <span>{sortOrder.toUpperCase()}</span>
                    <button
                      onClick={() => setSortOrder('a-z')}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#00ffff',
                        cursor: 'pointer',
                        padding: 0,
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: '1rem',
                        fontWeight: 'bold'
                      }}
                    >
                      ×
                    </button>
                  </div>
                )}

                {/* Event Type Badge */}
                {filterEventType !== 'all' && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.35rem 0.75rem',
                    background: 'rgba(0, 255, 255, 0.2)',
                    border: '1px solid rgba(0, 255, 255, 0.4)',
                    borderRadius: '20px',
                    fontSize: '0.8rem',
                    color: '#00ffff'
                  }}>
                    <span>{filterEventType.replace('-', ' ')}</span>
                    <button
                      onClick={() => setFilterEventType('all')}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#00ffff',
                        cursor: 'pointer',
                        padding: 0,
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: '1rem',
                        fontWeight: 'bold'
                      }}
                    >
                      ×
                    </button>
                  </div>
                )}

                {/* Genre Badge */}
                {filterGenre !== 'all' && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.35rem 0.75rem',
                    background: 'rgba(0, 255, 255, 0.2)',
                    border: '1px solid rgba(0, 255, 255, 0.4)',
                    borderRadius: '20px',
                    fontSize: '0.8rem',
                    color: '#00ffff'
                  }}>
                    <span>{filterGenre}</span>
                    <button
                      onClick={() => setFilterGenre('all')}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#00ffff',
                        cursor: 'pointer',
                        padding: 0,
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: '1rem',
                        fontWeight: 'bold'
                      }}
                    >
                      ×
                    </button>
                  </div>
                )}

                {/* Radius Badge */}
                {radiusFilter !== 'all' && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.35rem 0.75rem',
                    background: 'rgba(0, 255, 255, 0.2)',
                    border: '1px solid rgba(0, 255, 255, 0.4)',
                    borderRadius: '20px',
                    fontSize: '0.8rem',
                    color: '#00ffff'
                  }}>
                    <span>Within {radiusFilter} miles</span>
                    <button
                      onClick={() => setRadiusFilter('all')}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#00ffff',
                        cursor: 'pointer',
                        padding: 0,
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: '1rem',
                        fontWeight: 'bold'
                      }}
                    >
                      ×
                    </button>
                  </div>
                )}

                {/* Clear All Button */}
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setSortOrder('a-z');
                    setFilterEventType('all');
                    setFilterGenre('all');
                    setRadiusFilter('all');
                  }}
                  style={{
                    padding: '0.35rem 0.75rem',
                    background: 'rgba(255, 0, 128, 0.2)',
                    border: '1px solid rgba(255, 0, 128, 0.4)',
                    borderRadius: '20px',
                    color: '#ff0080',
                    cursor: 'pointer',
                    fontSize: '0.8rem',
                    fontWeight: '600',
                    marginLeft: 'auto'
                  }}
                >
                  Clear All
                </button>
              </div>
            )}
          </div>
          
          {getFilteredDJList().length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: 'rgba(255, 255, 255, 0.6)' }}>
              {searchQuery ? 'No DJs found matching your search' : 'No DJs with events yet'}
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' }}>
              {getFilteredDJList().map(dj => (
                <div
                  key={dj.user_id}
                  onClick={() => {
                    setSelectedDJ(dj);
                    setShowDJSchedule(true);
                    loadDJSchedule(dj.user_id);
                    loadRelationshipStatus(dj.user_id);
                  }}
                  style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '2px solid rgba(255, 0, 255, 0.3)',
                    borderRadius: '12px',
                    padding: '1.5rem',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 0, 255, 0.15)';
                    e.currentTarget.style.border = '2px solid rgba(255, 0, 255, 0.6)';
                    e.currentTarget.style.transform = 'translateY(-4px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                    e.currentTarget.style.border = '2px solid rgba(255, 0, 255, 0.3)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                    <div style={{
                      width: '50px',
                      height: '50px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #ff00ff, #00ffff)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.5rem'
                    }}>
                      🎧
                    </div>
                    <div style={{ flex: 1 }}>
                      <h4 style={{
                        fontSize: '1.1rem',
                        fontWeight: '700',
                        color: '#fff',
                        margin: 0
                      }}>
                        {dj.user_name}
                      </h4>
                      <p style={{
                        fontSize: '0.9rem',
                        color: 'rgba(255, 255, 255, 0.6)',
                        margin: '0.25rem 0 0 0'
                      }}>
                        {dj.event_count} {dj.event_count === 1 ? 'event' : 'events'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedDJ(dj);
                      setShowDJSchedule(true);
                      loadDJSchedule(dj.user_id);
                      loadRelationshipStatus(dj.user_id);
                    }}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: 'rgba(255, 0, 255, 0.2)',
                      border: '1px solid rgba(255, 0, 255, 0.4)',
                      borderRadius: '8px',
                      color: '#ff00ff',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 0, 255, 0.3)';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 0, 255, 0.2)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    View Full Schedule →
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* DJ Schedule Modal (Phase B2) */}
      <DJScheduleModal
        show={showDJSchedule && selectedDJ}
        onClose={() => {
          setShowDJSchedule(false);
          setSelectedDJ(null);
        }}
        selectedDJ={selectedDJ}
        relationshipStatus={relationshipStatus}
        setRelationshipStatus={setRelationshipStatus}
        djScheduleEvents={djScheduleEvents}
        setShowEventModal={setShowEventModal}
        setSelectedEvent={setSelectedEvent}
        handleFollow={handleFollow}
        handleUnfollow={handleUnfollow}
        handleFriendRequest={handleFriendRequest}
        renderReactionButtons={renderReactionButtons}
      />

      {/* Event Detail Modal */}
      <EventDetailModal
        show={showEventModal && selectedEvent}
        onClose={() => {
          setShowEventModal(false);
          setSelectedEvent(null);
        }}
        selectedEvent={selectedEvent}
        renderReactionButtons={renderReactionButtons}
        onOpenPromo={(promo) => {
          setSelectedPromo(promo);
          setShowPromoModal(true);
        }}
        onViewInMyEvents={onViewInMyEvents}
        onDiscordShare={onDiscordShare}
      />
      
      {/* Promo Navigation Modal */}
      {showPromoModal && selectedPromo && (
        <PromoNavigationModal
          promo={selectedPromo}
          onClose={() => {
            setShowPromoModal(false);
            setSelectedPromo(null);
          }}
          onNavigateToPromoSpot={() => {
            // Close DJ Booth and open Social Hub with Promo Spot
            setShowPromoModal(false);
            setSelectedPromo(null);
            if (onBack) onBack(); // Close DJ Booth
            
            // Trigger opening Social Hub -> Promo Spot
            setTimeout(() => {
              const promoSpotEvent = new CustomEvent('openPromoSpot', { detail: { promo: selectedPromo } });
              window.dispatchEvent(promoSpotEvent);
            }, 100);
          }}
        />
      )}


      {/* Add Event Modal */}
      <AddEventModal
        show={showAddModal}
        onClose={() => setShowAddModal(false)}
        eventForm={eventForm}
        handleFormChange={handleFormChange}
        handleSubmitEvent={handleSubmitEvent}
        formError={formError}
        formSuccess={formSuccess}
      />


      {/* Share Modal (Phase B3) */}
      <ShareModal
        show={showShareModal && eventToShare}
        onClose={() => {
          setShowShareModal(false);
          setEventToShare(null);
          setShareRecipients([]);
        }}
        eventToShare={eventToShare}
        shareRecipients={shareRecipients}
        setShareRecipients={setShareRecipients}
        followersList={followersList}
        friendsList={friendsList}
        handleShareSubmit={handleShareSubmit}
      />

      {/* Share to The Wall */}
      {wallShareEvent && ReactDOM.createPortal(
        <ShareToWallModal
          isOpen={!!wallShareEvent}
          onClose={() => setWallShareEvent(null)}
          source="calendar_event"
          eventItem={{
            title: wallShareEvent.event_name,
            subtitle: wallShareEvent.venue_name,
            date_line: (() => {
              try {
                const d = new Date(wallShareEvent.event_date);
                return `📅 ${d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}${wallShareEvent.event_time ? ` · ${wallShareEvent.event_time}` : ''}`;
              } catch { return null; }
            })(),
            location: wallShareEvent.location,
            genre: wallShareEvent.genre,
            link: wallShareEvent.ticket_url || wallShareEvent.stream_url || null,
          }}
          currentUser={(() => { try { return JSON.parse(localStorage.getItem('beatseek_user') || 'null'); } catch { return null; } })()}
        />,
        document.body
      )}
    </div>
  );
};

export default EventCalendarTab;
